<template>
  <b-modal id="card-preview-modal" class="card-preview-modal" v-on:shown="setup" v-on:hidden="clean">

    <div slot="modal-header" class="w-100">
      <h5 class="modal-title card-preview-modal__title">{{ cardTitle }}</h5>
      <button
        type="button"
        class="modal-close"
        @click="$root.$emit('hide::modal', 'card-preview-modal')"
      >
        <img src="/static/images/btn-close.svg">
      </button>
      <div class="row">
        <div class="col-6">
          <span v-show="showToggle">
            <ToggleSwitch v-on:change="toggleShowSolution" v-bind:model-value="showSolution" v-bind:check-value="true" id="show-solution-switch">
              <span slot="label">{{ $t('common.modal.preview.show_solution') }}</span>
            </ToggleSwitch>
          </span>
          <span class="divider"></span>
          <span class="typography--body-3 typography--color-secondary typography--font-weight-500 passive-link-btns clickable"
                v-on:click="showReportContentModal()"
                v-if="isStorePage">
            {{ $t('common.modal.preview.report_content') }}
          </span>
        </div>
      </div>
    </div>

    <!-- audio player -->
    <LcAudio v-bind:audio="getFormattedAudio(currentPage)" v-if="hasAudio(currentPage)"></LcAudio>

    <div class="typography--body-2 typography--color-disabled text-center mt-5"
         v-html="$t('common.modal.preview.no_pages')"
         v-if="hasNoPages">
    </div>

    <!-- modal body -->
    <div class="preview-content-container" v-bind:class="{ 'show-solution': showSolution }" v-if="onShown">
      <!-- Quiz badge -->
      <div class="quiz-badge" v-if="isQuizPage">
        <span class="badge badge-default">{{ $t('common.card.quiz') }}</span>
        <span class="typography--body-3 typography--color-secondary ml-1" v-if="isClassAssignmentPreview">
          {{ $t('common.modal.preview.class_average_score') }}: {{ getAverageQuizScoreRatio(currentPage.submitted_answers) }}%
        </span>
        <span class="typography--body-1 typography--color-secondary float-right">{{ $t('common.numeric_score', { n: currentPage.score || 1 }) }}</span>
      </div>

      <!-- Extra content -->
      <div
        v-if="hasExtraContent"
        class="preview-content"
      >
        {{currentPage.extra_content.title}}
        <div>
          <img class="extra-content-image" v-bind:src=currentPage.extra_content.image_url>
        </div>
      </div>

      <!-- video player -->
      <LcVideo v-bind:video="getFormattedVideo(currentPage)" v-if="hasVideo(currentPage)"></LcVideo>

      <!-- Page content -->
      <div
        v-if="currentPage"
        class="preview-content"
        v-html="parseHyperlink(currentPage.content)"
      >
      </div>

      <!-- Quiz answers -->
      <div class="quiz-answers" v-if="getPageType() === 'multiple_choice' && isVisibleQuizOptions()">
        <div v-for="(answer, index) in quizAnswers"
             v-bind:key="index"
             class="typography--content-body">
          {{ index + 1 }}. {{ getAnswerContent(answer) }}
        </div>
      </div>

      <div class="quiz-answer-result-container" v-if="isQuizPage && showSolution && !isEssayType">
        <!-- correct/incorrect answer header -->
        <div class="typography--content-body is-incorrect" v-if="isStudentAssignmentPreview && submittedAnswer">
          <strong v-if="submittedAnswer && submittedAnswer.is_correct">
            {{ $t('common.modal.preview.is_correct') }}
          </strong>
          <strong v-else>
            {{ $t('common.modal.preview.is_incorrect') }}
          </strong>
        </div>

        <div class="quiz-answer-result">
          <!-- correct answer -->
          <span class="typography--body-1">
            {{ $t('common.modal.preview.correct_answer') }}:
          </span>
          <span class="typography--body-1" v-if="getPageType() === 'multiple_choice'">
            {{ $t('common.modal.preview.answer_number', { n: correctAnswerIndex }) }}
          </span>
          <span class="typography--body-1" v-if="getPageType() === 'text_entry'">
            {{ correctAnswerContents }}
          </span>

          <!-- incorrect answer -->
          <span class="incorrect-answer-area" v-if="isStudentAssignmentPreview">
            <span class="typography--color-dividers">|</span>
            <span class="typography--body-1" v-if="getPageType() === 'multiple_choice'">
              {{ $t('common.modal.preview.chosen_answer') }}:
              <span v-if="submittedAnswer">
                {{ $t('common.modal.preview.answer_number', { n: submittedAnswerIndex }) }}
              </span>
              <span v-else>-</span>
            </span>
            <span class="typography--body-1" v-if="getPageType() === 'text_entry'">
              {{ $t('common.modal.preview.entered_answer') }}:
              <span v-if="submittedAnswer">
                {{ submittedAnswerContent }}
              </span>
              <span v-else>-</span>
            </span>
          </span>
        </div>

        <!-- incorrect mark image -->
        <div class="incorrect-img" v-if="isStudentAssignmentPreview && submittedAnswer && !submittedAnswer.is_correct">
          <img src="/static/images/quiz-wrong-s.svg">
        </div>

        <!-- correct mark image -->
        <div class="correct-img" v-if="isStudentAssignmentPreview && submittedAnswer && submittedAnswer.is_correct">
          <img src="/static/images/quiz-correct-s.png">
        </div>

        <!-- answer results for class assignment -->
        <div class="class-quiz-answer-results" v-if="isClassAssignmentPreview && currentPage.submitted_answers && currentPage.submitted_answers.length > 0">
          <div v-if="getPageType() === 'multiple_choice'">
            <div v-for="(answer, index) in quizAnswers"
                 v-bind:key="index"
                 class="typography--body-1 typography--color-secondary">
              {{ index + 1 }}.
              {{ getSumittedAnswerCount(answer) }}{{ $t('common.modal.preview.count') }}
              ({{ getSumittedAnswerRatio(getSumittedAnswerCount(answer)) }}%)
            </div>
          </div>

          <div v-if="getPageType() === 'text_entry'">
            <div v-for="(value, key) in summarizedSubmittedAnswers"
                 v-bind:key="key"
                 class="typography--body-1 typography--color-secondary">
              {{ key }} -
              {{ value.length }}{{ $t('common.modal.preview.count') }}
              ({{ getSumittedAnswerRatio(value.length) }}%)
            </div>
          </div>
        </div>
      </div>

      <!-- Quiz description -->
      <div class="quiz-description" v-if="isQuizPage && showSolution">
        <div class="typography--content-body description-header">
          <strong>{{ $t('common.card.page.description') }}</strong>
        </div>
        <div
          v-if="currentPage && !isEssayType && currentPage.description"
          class="preview-content"
          v-html="parseHyperlink(currentPage.description)"
        >
        </div>
        <div
          v-if="currentPage && isEssayType && currentPage.description"
          class="preview-content"
          v-html="parseHyperlink(currentPage.description.replace(/\n/g, '<br>').replace(/\\n/g, '<br>'))"
        >
        </div>
      </div>
    </div>


    <div slot="modal-footer" class="row">
      <!-- mobile btns -->
      <div class="col-5 hidden-sm-up">
        <button type="button"
                class="btn btn-outline-primary btn-fab btn-mini-fab mr-1"
                v-bind:disabled="currentPageIndex === 0"
                v-on:click="prev"
                v-if="!isPagePreview">
          <i class="material-icons">arrow_back</i>
        </button>
        <button type="button"
                class="btn btn-outline-primary btn-fab btn-mini-fab"
                v-bind:disabled="isLastPage"
                v-on:click="next"
                v-if="!isPagePreview">
          <i class="material-icons">arrow_forward</i>
        </button>
      </div>

      <!-- tablet & desktop btns -->
      <div class="col-5 hidden-xs-down">
        <button type="button"
                class="btn btn-outline-primary mr-1"
                v-bind:disabled="currentPageIndex === 0"
                v-on:click="prev"
                v-if="!isPagePreview">
          {{ $t('common.previous') }}
        </button>
        <button type="button"
                class="btn btn-outline-primary"
                v-bind:disabled="isLastPage"
                v-on:click="next"
                v-if="!isPagePreview">
          {{ $t('common.next') }}
        </button>
      </div>

      <div class="col-2 text-center page-counter">
        <small class="typography--body-3 typography--color-secondary" v-if="!isPagePreview">
          {{ currentPageIndex + 1 }}/{{ cardPagesLength }}
        </small>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { groupBy, isEmpty, isNil, every, isNaN, isNumber } from 'lodash';
  import parses from '@/mixins/parses';

  export default {
    name: 'card-preview',

    mixins: [
      parses,
    ],

    data() {
      return {
        answerList: [],

        currentPageIndex: 0,

        showSolution: false,

        onShown: true,
      };
    },

    computed: {
      ...mapGetters({
        isStudentAssignmentPreview: 'isStudentAssignmentPreview',
        isClassAssignmentPreview: 'isClassAssignmentPreview',
        selectedPageIndex: 'selectedPreviewPageIndex',
        isPagePreview: 'isPagePreview',
        card: 'selectedPreviewCard',
      }),

      showToggle() {
        return !this.isStudentAssignmentPreview && !this.isClassAssignmentPreview;
      },

      cardTitle() {
        return this.card ? this.card.title : '';
      },

      currentPage() {
        if (this.card && !isEmpty(this.card.pages) && this.card.pages[this.currentPageIndex]) {
          return this.card.pages[this.currentPageIndex];
        }

        return {};
      },

      hasNoPages() {
        if (this.card && this.card.pages && this.card.pages.length > 0) {
          return false;
        }

        return true;
      },

      currentPageAnswer() {
        return this.answerList[this.currentPageIndex];
      },

      isStorePage() {
        return !isNil(this.currentPage.store_page_id);
      },

      isQuizPage() {
        if (this.card) {
          return this.currentPage.is_quiz;
        }

        return false;
      },

      isEssayType() {
        if (this.isQuizPage) {
          return this.getPageType() === 'essay_type';
        }

        return false;
      },

      hasExtraContent() {
        return !isEmpty(this.currentPage.extra_content);
      },

      submittedEssayAnswer() {
        if (this.isEssayType && this.submittedAnswer && this.submittedAnswer.value) {
          if (!this.currentPage.grading_keywords) {
            return this.submittedAnswer.value.replace(/\n/g, '<br>');
          }

          /* eslint-disable newline-per-chained-call */
          let answer = this.submittedAnswer.value.replace(/\n/g, '<br>');
          const gradingKeywords = this.currentPage.grading_keywords.replace(/\?/g, '\\?')
            .replace(/\^/g, '\\^').replace(/\./g, '\\.').replace(/\$/g, '\\$').replace(/\|/g, '\\|')
            .replace(/\*/g, '\\*').replace(/\+/g, '\\+').replace(/\(/g, '\\(').replace(/\)/g, '\\)')
            .replace(/\[/g, '\\[').replace(/\]/g, '\\]').replace(/\{/g, '\\{').replace(/\}/g, '\\}');
          /* eslint-enable */

          gradingKeywords.split(',').forEach((keyword) => {
            answer = answer.replace(new RegExp(keyword, 'g'), `<span class="typography--color-accent typography--font-weight-600">${keyword}</span>`);
          });

          return answer;
        }

        return null;
      },

      quizPageList() {
        if (this.card) {
          return this.card.pages.filter(page => (!!page.is_quiz));
        }

        return [];
      },

      quizAnswers() {
        if (this.isQuizPage) {
          return this.currentPage.options || this.currentPage.answers;
        }

        return [];
      },

      submittedAnswer() {
        if (this.currentPage.submitted_answers && this.currentPage.submitted_answers.length > 0) {
          return this.currentPage.submitted_answers[0];
        }

        return null;
      },

      summarizedSubmittedAnswers() {
        if (this.currentPage.submitted_answers && this.currentPage.submitted_answers.length > 0) {
          return groupBy(this.currentPage.submitted_answers, 'value');
        }

        return null;
      },

      correctAnswerIndex() {
        let correctIndex = -1;

        this.quizAnswers.forEach((answer, i) => {
          if (this.getIsCorrect(answer)) {
            correctIndex = i;
          }
        });

        return correctIndex + 1;
      },

      correctAnswerContents() {
        return this.quizAnswers.filter(answer => (
          !!this.getIsCorrect(answer)
        ))
        .map(answer => (this.getAnswerContent(answer))).join(', ');
      },

      submittedAnswerIndex() {
        if (!this.submittedAnswer) {
          return 0;
        }

        let submittedIndex = -1;

        this.quizAnswers.forEach((answer, i) => {
          if (this.submittedAnswer.answer_id === answer.id) {
            submittedIndex = i;
          }
        });

        return submittedIndex + 1;
      },

      submittedAnswerContent() {
        if (!this.submittedAnswer) {
          return '';
        }

        return this.submittedAnswer.value;
      },

      isLastPage() {
        if (this.card) {
          return this.currentPageIndex === (this.card.pages.length - 1);
        }

        return 0;
      },

      cardPagesLength() {
        return this.card ? this.card.pages.length : 0;
      },

      extraContentImgaeBinding() {
        if (this.currentPage.extra_content.image_url) {
          return {
            'background-image': `url('${this.currentPage.extra_content.image_url}')`,
          };
        }
        return {};
      },
    },

    methods: {
      ...mapActions([
        'setIsStudentAssignmentPreview',
        'setIsClassAssignmentPreview',
        'setSelectedPreviewPageIndex',
        'setIsPagePreview',
      ]),

      showReportContentModal() {
        this.setSelectedPreviewPageIndex(this.currentPageIndex)
          .then(() => (
            this.$emit('show-report-content-modal')
          ));
      },

      setup() {
        this.onShown = true;
        this.answerList = this.card.pages.map((page) => {
          if (page.is_quiz) {
            return { pageId: page.id, answer: null };
          }

          return {};
        });

        this.currentPageIndex = this.selectedPageIndex;
        this.showSolution = false;

        if (this.isClassAssignmentPreview || this.isStudentAssignmentPreview) {
          this.showSolution = true;
        }
      },

      clean() {
        this.onShown = false;
        this.answerList = [];
        const pageElem = document.querySelector('#card-preview-modal .modal-body>div');

        if (pageElem) {
          pageElem.scrollIntoView(true);
        }

        this.setIsStudentAssignmentPreview(false);
        this.setIsClassAssignmentPreview(false);
        this.setIsPagePreview(false);
      },

      prev() {
        if (this.currentPageIndex > 0) {
          this.currentPageIndex -= 1;
          document.querySelector('#card-preview-modal .modal-body>div').scrollIntoView(true);
        }
      },

      next() {
        if (!this.isLastPage) {
          this.currentPageIndex += 1;
          document.querySelector('#card-preview-modal .modal-body>div').scrollIntoView(true);
        }
      },

      hasAudio(page) {
        return page.audio_count > 0 && page.audios.length > 0;
      },

      hasVideo(page) {
        return page.video_count > 0 && page.videos && page.videos.length > 0;
      },

      getFormattedAudio(page) {
        if (!this.hasAudio(page)) {
          return null;
        }

        const audio = page.audios[0];

        if (audio.audio_url) {
          return audio;
        }

        return {
          duration_millis: audio.duration_millis,
          audio_url: audio.url,
          created_at: audio.created_at,
          deleted_at: null,
          duration: audio.duration,
          file_size: audio.file_size,
          id: audio.id,
          original_filename: audio.original_filename,
          page_id: page.id,
          reference: audio.reference,
          updated_at: audio.updated_at,
        };
      },

      getFormattedVideo(page) {
        if (!this.hasVideo(page)) {
          return null;
        }

        const video = page.videos[0];

        if (video.video_url) {
          return video;
        }

        return {
          duration_millis: video.duration_millis,
          video_url: video.url,
          created_at: video.created_at,
          deleted_at: null,
          duration: video.duration,
          file_size: video.file_size,
          id: video.id,
          width: video.width,
          height: video.height,
          page_id: page.id,
          poster_url: video.poster_url,
          reference: video.reference,
          updated_at: video.updated_at,
        };
      },

      toggleShowSolution(value) {
        this.showSolution = value;
      },

      getIsCorrect(answer) {
        if (answer.is_correct !== undefined) {
          return answer.is_correct;
        }

        return answer.is_answer;
      },

      getIsIncorrect(answer) {
        if (this.getSumittedAnswerCount(answer) === 0) {
          return false;
        }

        return !this.getIsCorrect(answer);
      },

      getAnswerContent(answer) {
        if (answer.content !== undefined) {
          return answer.content;
        }

        return answer.value;
      },

      getPageType() {
        if (this.currentPage.quiz_type) {
          return this.currentPage.quiz_type;
        }

        if (this.currentPage.page_type) {
          return this.currentPage.page_type.name;
        }

        if (this.currentPage.page_type_id === 2) {
          return 'multiple_choice';
        }

        if (this.currentPage.page_type_id === 3) {
          return 'text_entry';
        }

        if (this.currentPage.page_type_id === 4) {
          return 'essay_type';
        }

        return '';
      },

      isVisibleQuizOptions() {
        return this.currentPage.answer_view_type !== 'control' || (!this.isEmptyOptions() && !this.isInvalidNumberOptions());
      },

      isInvalidNumberOptions() {
        return every(this.quizAnswers, (item) => {
          const option = Number(this.getOption(item));
          return !isNaN(option)
              && isNumber(option)
              && this.getOption(item).length === 1;
        });
      },

      isEmptyOptions() {
        return every(this.quizAnswers, answer => !answer.content);
      },

      getOption(option) {
        return option.content || option.value;
      },

      getAverageQuizScoreRatio(submittedAnswers) {
        if (!submittedAnswers || submittedAnswers.length === 0) {
          return 0;
        }

        const correctCount = submittedAnswers.filter(answer => (
          !!answer.is_correct
        )).length;

        return parseInt((correctCount / submittedAnswers.length) * 100, 10);
      },

      getSumittedAnswerCount(answer) {
        if (this.currentPage.submitted_answers && this.currentPage.submitted_answers.length > 0) {
          return this.currentPage.submitted_answers.filter(submittedAnswer => (
            submittedAnswer.answer_id === answer.id
          )).length;
        }

        return 0;
      },

      getSumittedAnswerRatio(count) {
        if (this.currentPage.submitted_answers && this.currentPage.submitted_answers.length > 0) {
          return parseInt((count / this.currentPage.submitted_answers.length) * 100, 10);
        }

        return 0;
      },
    },
  };
</script>

<style lang="scss">
  .card-preview-modal {
    .extra-content-image {
      margin: 8px 0;
      border: 1px solid #BBB;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/CardPreview.vue